import { AxiosError } from "axios";
import { TelehealthSessionResponse } from "../../../types/generated/models";
import { useClientPortalConfigContext } from "../../../context/ClientPortalConfig.ctx";
import { useLocation } from "react-router";
import { ROUTES } from "../../../routes";
import { is400Error, useGetQuery } from "../../../hooks/useClientPortalQuery";

type RouteType = "telehealth" | "telehealthplus" | "unknown";

function getRouteTypeFromPath(pathname: string): RouteType {
    if (pathname.includes(`${ROUTES.TELEHEALTH_PLUS.urlPath}/`)) {
        return "telehealthplus";
    } else if (pathname.includes(`${ROUTES.TELEHEALTH.urlPath}/`)) {
        return "telehealth";
    }
    return "unknown";
}

export function useTelehealthSession(id: string | undefined) {
    const { clientPortalGuid } = useClientPortalConfigContext();
    const location = useLocation();

    const routeType = getRouteTypeFromPath(location.pathname);

    const endpoint =
        routeType === "telehealth"
            ? `/api/v1/${clientPortalGuid}/Telehealth/GetAllClientTelehealthSessions/${id}`
            : `/api/v1/${clientPortalGuid}/Telehealth/GetTelehealthSessionByRoomId/${id}`;

    const { data, isLoading, isError, error, refetch } = useGetQuery<TelehealthSessionResponse[]>({
        url: endpoint,
        // Failures are shown inline on the Telehealth page (loading / retry / invalid-link states),
        // so suppress the default global snackbar and not-available redirect.
        handleCustomError: () => {},
    });

    return {
        sessions: data ?? [],
        isLoading,
        isError,
        // A 4xx (invalid public id, expired link, or an inactive/archived client) means the link is
        // no longer usable; anything else (network / 5xx) is a transient "couldn't load" the client can retry.
        isInvalidLink: isError && is400Error(error as AxiosError),
        refetch,
    };
}
